<template>
  <div>
    <form enctype="multipart/form-data" novalidate v-if="isInitial || isSaving || isFailed">
      <h5 v-if="showHeader">{{ label }}</h5>
      <div class="mt-3">
        <UploadDrop
          v-if="uploadDrop"
          ref="multiInputDrop"
          :name="uploadFieldName"
          :accept="accept"
          :loading="loading"
          :disabled="isSaving"
          :isFailed="isFailed"
          :isSuccess="isSuccess"
          :fileCount="fileCount"
          :isMultiple="multiple"
          @change="onSelectFile"
        />
        <div>
          <label :for="_uid" :class="selectBtnClass || 'mb-0 btn btn-secondary '">
            {{ selectBtnLabel || `Choose File${multiple ? '(s)' : ''}` }}
          </label>
          <span class="ml-3" v-if="!uploadedFiles.length">No File Chosen</span>
          <div class="mr-3" style="width: 300px" v-if="v_hasUploadFromLink">
            <UploadFromLink
              :hasLabel="false"
              :hasUploadBtn="false"
              :compress="true"
              :allowChangeLinkEvent="true"
              :uploadToStoreMedia="uploadToStoreMedia"
              :validate="{ required: true, url: true }"
              :folder="`${params.storeId}/pages/${params.pageId}`"
              :hasSpinner="true"
              @select="uploadedImageFromLink"
            />
          </div>
        </div>
        <input
          v-if="multiple"
          type="file"
          ref="multiInput"
          :id="_uid"
          multiple
          @click="resetUploader('multiInput')"
          :name="uploadFieldName"
          :disabled="isSaving"
          :accept="accept"
          @change="onSelectFile"
          class="input-file"
        />
        <input
          v-else
          type="file"
          ref="singleInput"
          :accept="accept"
          :id="_uid"
          @click="resetUploader('singleInput')"
          :name="uploadFieldName"
          :disabled="isSaving"
          @change="onSelectFile"
          class="input-file"
        />
      </div>
    </form>
    <!--SUCCESS-->
    <div v-if="isSuccess">
      <h2>Uploaded {{ uploadedFiles.length }} file{{ multiple ? '(s)' : '' }} successfully.</h2>
      <div>
        <a href="javascript:void(0)" @click="reset()">Upload again</a>
      </div>
      <ul class="list-unstyled">
        <li v-for="item in uploadedFiles" :key="item.url">
          <img :src="item.url" class="img-responsive img-thumbnail" :alt="item.originalName" />
        </li>
      </ul>
    </div>
    <!--FAILED-->
    <div v-if="isFailed">
      <b-alert variant="warning">Upload Failed</b-alert>
      <div>
        <a href="javascript:void(0)" @click="reset()">Try again</a>
      </div>
      <pre>{{ uploadError }}</pre>
    </div>
    <div v-if="uploadedFiles.length" class="mt-3 card p-3 bg-light">
      <h6>Attached File{{ multiple ? '(s)' : '' }}</h6>

      <div class="card my-3">
        <ul class="list-group list-group-flush" v-if="!hasInputs">
          <li v-for="(doc, index) in uploadedFiles" :key="index" class="list-group-item align-items-center">
            <div>
              <i class="far fa-file mr-2"></i>
              {{ doc.name }}
            </div>
            <div class="d-flex justify-content-between mt-2">
              <div v-if="allowCompress && isImageType(doc.file)" class="custom-control custom-checkbox">
                <input
                  v-model="doc.compress"
                  class="custom-control-input"
                  :id="`styled-checkbox-${index}`"
                  type="checkbox"
                />
                <label class="custom-control-label" :for="`styled-checkbox-${index}`">Compress</label>
              </div>
              <div>
                <a @click="uploadedFiles.splice(index, 1)">
                  <i class="far fa-trash mr-2"></i>
                </a>
              </div>
            </div>
          </li>
        </ul>
        <slot v-else name="inputs" :uploadedFiles="uploadedFiles"></slot>
      </div>
      <button type="button" @click="saveUploads" class="btn btn-primary" :disabled="loading">
        <i v-if="loading" class="far fa-spinner-third fa-spin mr-2"></i> Upload File{{ multiple ? '(s)' : '' }}
      </button>
    </div>
  </div>
</template>

<script>
import { uploadMixin } from '../../src/mixins/uploadMixin';
import UploadFromLink from '../../libs/components/UploadFromLink';
import UploadDrop from './UploadDrop.vue';

export default {
  components: {
    UploadDrop,
    UploadFromLink
  },
  mixins: [uploadMixin],
  methods: {
    resetUploader(ref) {
      this.$refs[ref].value = '';
    },
    async saveUploads() {
      await this.$validator.validateAll();
      if (this.errors.any()) return;
      this.initFormData();
      const { data } = await this.uploadDocuments(this.uploadMedia);
      this.$emit('successUpload', data);
      this.uploadedFiles = [];
    },
    initFormData() {
      this.setUpFormData(this.uploadedFiles);
      for (const prop in this.params) {
        this.formData.set(prop, this.params[prop]);
      }
      this.formData.set(
        'files',
        JSON.stringify(
          this.uploadedFiles.map((f) => {
            delete f.file;
            delete f.size;
            delete f.fileType;
            delete f.type;
            return JSON.stringify(f);
          })
        )
      );
    },
    onSelectFile($event) {
      if (!this.multiple) {
        this.uploadedFiles = [];
      }
      console.log($event.target.name);
      this.filesChange($event.target.name, $event.target.files);
      this.fileCount = $event.target.files.length;
    },
    async uploadedImageFromLink(image) {
      const { data } = await this.addMedia({ ...this.params, files: [image] });
      this.$emit('successUpload', data);
    },
    isImageType(file) {
      return file && file.type && ['image/jpeg', 'image/gif', 'image/png', 'image/webp'].includes(file.type);
    }
  },
  props: {
    label: {
      type: String,
      default: 'Media'
    },
    accept: {
      type: String,
      default: 'all'
    },
    multiple: {
      type: Boolean,
      default: true
    },
    uploadMedia: {
      type: Function,
      required: true
    },
    addMedia: {
      type: Function
    },
    params: {
      type: Object,
      required: true
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    allowCompress: {
      type: Boolean,
      default: false
    },
    selectBtnLabel: {
      type: String
    },
    selectBtnClass: {
      type: String
    },
    hasInputs: {
      type: Boolean,
      default: false
    },
    uploadDrop: {
      type: Boolean,
      default: false
    }
  }
};
</script>
